import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useMemo } from "react";
import { defaultTheme, themes } from "../core/theme/themes";
import { mergeTheme, generateCssVariablesObject } from "../utils";
import { MedosClient } from "../client/MedosClient";
const ThemeContext = createContext(undefined);
export function MedosThemeProvider({ children, theme, cssVariablePrefix = "medos", onThemeError, }) {
    const [apiTheme, setApiTheme] = React.useState(null);
    React.useEffect(() => {
        if (theme !== undefined) {
            return;
        }
        let isMounted = true;
        const fetchThemeFromApi = async () => {
            try {
                const fetchedTheme = await MedosClient.fetchTheme();
                if (isMounted) {
                    if (fetchedTheme &&
                        (fetchedTheme === "default" || fetchedTheme === "modern")) {
                        setApiTheme(fetchedTheme);
                    }
                    else {
                        setApiTheme(null);
                    }
                }
            }
            catch (error) {
                if (isMounted) {
                    if (onThemeError) {
                        onThemeError(error);
                    }
                    else {
                        console.warn("Failed to fetch theme from API, using default theme:", error);
                    }
                }
            }
        };
        fetchThemeFromApi();
        return () => {
            isMounted = false;
        };
    }, [theme, onThemeError]);
    const resolvedTheme = useMemo(() => {
        if (theme !== undefined) {
            if (typeof theme === "string") {
                return themes[theme] || defaultTheme;
            }
            if ("name" in theme && "colors" in theme && "typography" in theme) {
                return theme;
            }
            return mergeTheme(theme);
        }
        if (apiTheme) {
            return themes[apiTheme] || defaultTheme;
        }
        return defaultTheme;
    }, [theme, apiTheme]);
    const themeName = useMemo(() => {
        if (typeof theme === "string") {
            return theme;
        }
        if (theme && "name" in theme) {
            return theme.name;
        }
        if (apiTheme) {
            return apiTheme;
        }
        return "default";
    }, [theme, apiTheme]);
    const cssVars = useMemo(() => generateCssVariablesObject(resolvedTheme, cssVariablePrefix), [resolvedTheme, cssVariablePrefix]);
    const contextValue = useMemo(() => ({
        theme: resolvedTheme,
        cssVars,
        prefix: cssVariablePrefix,
        themeName,
    }), [resolvedTheme, cssVars, cssVariablePrefix, themeName]);
    return (_jsx(ThemeContext.Provider, { value: contextValue, children: _jsx("div", { style: cssVars, "data-theme": themeName, className: "medos-sdk-root", children: children }) }));
}
export function useThemeContext() {
    const context = React.useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeContext must be used within a MedosThemeProvider");
    }
    return context;
}
export function useTheme() {
    const context = React.useContext(ThemeContext);
    return context?.theme || defaultTheme;
}
export function useCssVar(category, key, fallback) {
    const context = React.useContext(ThemeContext);
    const prefix = context?.prefix || "medos";
    const varName = `--${prefix}-${category}-${key
        .replace(/([A-Z])/g, "-$1")
        .toLowerCase()}`;
    return fallback ? `var(${varName}, ${fallback})` : `var(${varName})`;
}
export function useThemeName() {
    const context = React.useContext(ThemeContext);
    return context?.themeName || "default";
}
